import React from 'react';
import Layout from '@/components/layout/Layout';
import PageHeader from '@/components/shared/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Shield, CheckCircle2, Globe, Calendar, Award, Users } from 'lucide-react';
import { team, coreValues, brandStory, mission, safetyProgram, companyHistory, founderBio } from '@/data/mock';

const AboutPage = () => {
  const founder = team.find(member => member.isFounder);
  const executives = team.filter(member => !member.isFounder);

  return (
    <Layout>
      <PageHeader
        title="About Redspire"
        subtitle="From European roots to American infrastructure — engineering strength and building tomorrow."
        breadcrumb="About"
        backgroundImage="https://images.unsplash.com/photo-1536895058696-a69b1c7ba34f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxjaXZpbCUyMGVuZ2luZWVyaW5nfGVufDB8fHx8MTc2NDE0MzU2OHww&ixlib=rb-4.1.0&q=85"
      />

      {/* Brand Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Globe className="w-6 h-6 text-red-600" />
                <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Our Story</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-6">
                From Prague to Connecticut: Building the Foundations of Progress
              </h2>
              <div className="space-y-4 text-lg text-gray-600 leading-relaxed">
                {brandStory.split('\n\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1527335988388-b40ee248d80c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxpbmZyYXN0cnVjdHVyZXxlbnwwfHx8fDE3NjQxNDM1NzN8MA&ixlib=rb-4.1.0&q=85"
                  alt="Infrastructure construction"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-red-600 rounded-2xl -z-10" />
              <div className="absolute -top-4 -right-4 bg-white rounded-xl shadow-lg p-4">
                <div className="text-center">
                  <span className="text-3xl font-bold text-red-600">1998</span>
                  <p className="text-sm text-gray-600">Founded</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Timeline */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Our Journey</span>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold text-gray-900">Company History</h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              From our founding in the Czech Republic to our Connecticut headquarters today.
            </p>
          </div>
          <div className="relative">
            {/* Timeline line */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-red-200 -translate-x-1/2" />
            
            <div className="space-y-12">
              {companyHistory.map((event, index) => (
                <div
                  key={index}
                  className={`flex flex-col md:flex-row items-center gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <Card className="border-0 shadow-md hover:shadow-lg transition-shadow inline-block">
                      <CardContent className="p-6">
                        <Badge className="bg-red-600 hover:bg-red-700 mb-3">{event.year}</Badge>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">{event.title}</h3>
                        <p className="text-gray-600">{event.description}</p>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="relative z-10">
                    <div className="w-4 h-4 rounded-full bg-red-600 border-4 border-white shadow" />
                  </div>
                  <div className="flex-1" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Founder Image */}
            <div className="lg:col-span-2">
              <div className="sticky top-24">
                <div className="aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl mb-6 bg-gray-900">
                  <img
                    src={founderBio.image}
                    alt={founderBio.imageAlt || founderBio.name}
                    className={`w-full h-full ${founderBio.isPlaceholder ? 'object-contain p-4' : 'object-cover'}`}
                  />
                </div>
                <div className="bg-gray-50 rounded-xl p-6">
                  <h4 className="font-semibold text-gray-900 mb-4">Career Highlights</h4>
                  <ul className="space-y-3">
                    {founderBio.highlights.map((highlight, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600 text-sm">{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Founder Bio */}
            <div className="lg:col-span-3">
              <div className="flex items-center gap-3 mb-4">
                <Award className="w-6 h-6 text-red-600" />
                <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Founder</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{founderBio.name}</h2>
              <p className="text-xl text-red-600 font-medium mb-8">{founderBio.title}</p>
              
              <div className="prose prose-lg max-w-none">
                {founderBio.fullBio.split('\n\n').map((paragraph, index) => (
                  <p key={index} className="text-gray-600 leading-relaxed mb-6">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <span className="text-red-500 font-semibold text-sm uppercase tracking-wider">Our Mission</span>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold leading-tight">
              Engineering for Generations
            </h2>
            <p className="mt-8 text-xl text-gray-300 leading-relaxed">
              {mission}
            </p>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Our Values</span>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold text-gray-900">What Drives Us</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {coreValues.map((value, index) => (
              <Card
                key={index}
                className="group border-0 shadow-sm hover:shadow-lg transition-shadow duration-300"
              >
                <CardContent className="p-8">
                  <div className="w-3 h-12 bg-red-600 rounded-full mb-6 group-hover:h-16 transition-all duration-300" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Users className="w-6 h-6 text-red-600" />
              <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Leadership</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Our Executive Team</h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              Experienced leaders driving excellence in civil engineering and infrastructure development.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {executives.map((member) => (
              <Card
                key={member.id}
                className="group overflow-hidden border-0 shadow-sm hover:shadow-xl transition-all duration-300"
              >
                <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                  <img
                    src={member.image}
                    alt={member.imageAlt || member.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900">{member.name}</h3>
                  <p className="text-red-600 font-medium text-sm mb-4">{member.title}</p>
                  <p className="text-gray-600 text-sm">{member.bio}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Safety Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-xl bg-red-600 flex items-center justify-center">
                  <Shield className="w-7 h-7 text-white" />
                </div>
                <span className="text-red-500 font-semibold text-sm uppercase tracking-wider">
                  Safety First Program
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold leading-tight mb-6">
                Zero Compromise on Safety
              </h2>
              <p className="text-lg text-gray-300 leading-relaxed mb-8">
                Safety is the foundation of every decision we make. Our Safety-First Program, 
                rooted in European engineering discipline and enhanced through decades of experience, 
                ensures every team member returns home safely every day.
              </p>
              <ul className="space-y-4">
                {safetyProgram.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxjaXZpbCUyMGVuZ2luZWVyaW5nfGVufDB8fHx8MTc2NDE0MzU2OHww&ixlib=rb-4.1.0&q=85"
                  alt="Safety on site"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-red-600/20 rounded-2xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="aspect-video rounded-2xl overflow-hidden shadow-xl bg-gray-200">
                <div className="w-full h-full flex items-center justify-center bg-gray-100">
                  <div className="text-center">
                    <Globe className="w-16 h-16 text-red-600 mx-auto mb-4" />
                    <p className="text-gray-900 font-semibold text-lg">Stamford, Connecticut</p>
                    <p className="text-gray-600">U.S. Headquarters</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Our Location</span>
              <h2 className="mt-3 text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
                Connecticut Headquarters
              </h2>
              <p className="mt-6 text-lg text-gray-600 leading-relaxed">
                After establishing our foundation in the Czech Republic and building a reputation 
                across European markets, Redspire chose Stamford, Connecticut as our permanent 
                U.S. headquarters. This strategic location provides access to major Northeast 
                infrastructure corridors while maintaining the focused, community-oriented approach 
                that defines our work.
              </p>
              <div className="mt-8 p-6 bg-gray-50 rounded-xl">
                <p className="font-semibold text-gray-900 mb-2">Redspire Civil Engineering</p>
                <p className="text-gray-600">218 Brookside Industrial Park</p>
                <p className="text-gray-600">Stamford, CT 06902</p>
                <p className="text-gray-600">United States</p>
                <p className="text-red-600 font-medium mt-3">+1 (203) 496-3375</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AboutPage;
